import { PLAY_SESSIONS_ENABLED } from '../data/api/config';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { discoverWallets, type InjectedInfo } from '../data/chain/wallet';

/**
 * Connect-wallet dialog: wallets the browser announces (EIP-6963, with their own name and icon) first, then install
 * links for popular wallets that are not detected. Escape, the close button or a click outside closes it; on phones it
 * is a bottom sheet. Connecting only asks for an address; prompt-free trades require a separate bounded session authorization.
 */
const POPULAR: { name: string; rdns: string; url: string }[] = [
  { name: 'MetaMask', rdns: 'io.metamask', url: 'https://metamask.io/download/' },
  { name: 'Rabby', rdns: 'io.rabby', url: 'https://rabby.io/' },
  { name: 'Phantom', rdns: 'app.phantom', url: 'https://phantom.com/download' },
  { name: 'Coinbase Wallet', rdns: 'com.coinbase.wallet', url: 'https://www.coinbase.com/wallet/downloads' },
];

export function WalletModal({ networkName, onPick, onClose }: { networkName: string; onPick: (walletId?: string) => void; onClose: () => void }) {
  const [wallets, setWallets] = useState<InjectedInfo[]>(() => discoverWallets());
  const panel = useRef<HTMLDivElement>(null);
  // Latest onClose via a ref: the first button gets focus once, on open, not on every live update.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    // Wallets announce themselves asynchronously: look again shortly after opening.
    const ts = [150, 600].map((ms) => setTimeout(() => setWallets(discoverWallets()), ms));
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    window.addEventListener('keydown', onKey);
    panel.current?.querySelector<HTMLElement>('button')?.focus();
    return () => {
      ts.forEach(clearTimeout);
      window.removeEventListener('keydown', onKey);
      previousFocus?.focus();
    };
  }, []);
  const installed = new Set(wallets.map((w) => w.rdns));
  const missing = POPULAR.filter((p) => !installed.has(p.rdns));

  // Rendered at the page root: inside the header it would stay under the phone's bottom navigation.
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal wallet-modal" role="dialog" aria-modal="true" aria-labelledby="wallet-modal-h" ref={panel} data-testid="wallet-picker">
        <div className="modal-head">
          <h2 id="wallet-modal-h">Connect a wallet</h2>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        {wallets.length > 0 ? (
          <ul className="wallet-list" aria-label="Detected wallets">
            {wallets.map((w) => (
              <li key={w.uuid}>
                <button type="button" className="wallet-option" onClick={() => onPick(w.uuid === 'legacy' ? undefined : w.uuid)}>
                  {w.icon ? <img src={w.icon} alt="" width={32} height={32} /> : <span className="wallet-icon-fallback" aria-hidden="true">◆</span>}
                  <span className="wallet-name">{w.name}</span>
                  <span className="tag info">Detected</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="small">No browser wallet found. Install one below, then reload this page.</p>
        )}
        {missing.length > 0 && (
          <>
            <h3 className="wallet-more">{wallets.length > 0 ? 'Other wallets' : 'Get a wallet'}</h3>
            <ul className="wallet-list" aria-label="Install a wallet">
              {missing.map((p) => (
                <li key={p.rdns}>
                  <a className="wallet-option" href={p.url} target="_blank" rel="noopener noreferrer">
                    <span className="wallet-icon-fallback" aria-hidden="true">
                      {p.name[0]}
                    </span>
                    <span className="wallet-name">{p.name}</span>
                    <span className="small muted">Install ↗</span>
                  </a>
                </li>
              ))}
            </ul>
          </>
        )}
        <p className="small muted wallet-foot">
          RUNNER runs on {networkName}. Connecting shares your address only. {PLAY_SESSIONS_ENABLED ? 'Trades need wallet confirmation unless you authorize a bounded play session.' : 'Every trade needs your wallet confirmation.'}
        </p>
      </div>
    </div>,
    document.body,
  );
}
